/**
 * RegulatoryStackStep — Step 2 of setup wizard.
 *
 * Shows the product's required RuleSets (DRR-090/096) — legislation bundles
 * with statutory references. These are DETERMINISTIC from the product definition,
 * not LLM-guessed. RuleSets are locked (legal requirements cannot be removed).
 *
 * Each RuleSet shows: name, statute, affected workflows, jurisdiction.
 * User acknowledges the regulatory stack before proceeding.
 */

import { useMemo } from 'react';
import {
  Stack,
  Group,
  Text,
  Paper,
  Badge,
  Alert,
  ThemeIcon,
  Checkbox,
  Divider,
  SimpleGrid,
  List,
  Tooltip,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconLock,
  IconScale,
  IconInfoCircle,
  IconGavel,
  IconBuilding,
  IconBuildingBank,
} from '@tabler/icons-react';
import type { ProductDefinition } from './ProductSelectionStep';

export interface RegulatoryStackStepProps {
  product: ProductDefinition;
  acknowledged: boolean;
  onAcknowledge: (acknowledged: boolean) => void;
}

/**
 * RuleSet metadata — maps RuleSet IDs to human-readable details.
 * This comes from seed data; hardcoded here until backend provides
 * a `ruleSetDefinitions` query. Marked for backend migration.
 */
const RULESET_DETAILS: Record<string, {
  name: string;
  statute?: string;
  jurisdiction: 'federal' | 'state' | 'payer';
  authority: string;
  memberRulesCount: number;
  affectedWorkflowsCount: number;
  description: string;
}> = {
  'rs-4-0-001': {
    name: 'HIPAA Privacy & Security',
    statute: '45 CFR Parts 160, 162, 164',
    jurisdiction: 'federal',
    authority: 'HHS OCR',
    memberRulesCount: 12,
    affectedWorkflowsCount: 8,
    description: 'Protected health information safeguards, breach notification, and security standards.',
  },
  'rs-4-0-002': {
    name: 'CMS Conditions of Participation',
    statute: '42 CFR Part 483',
    jurisdiction: 'federal',
    authority: 'CMS',
    memberRulesCount: 8,
    affectedWorkflowsCount: 6,
    description: 'Federal conditions for Medicare/Medicaid participation in long-term care facilities.',
  },
  'rs-4-1-004': {
    name: 'MN 144G — Assisted Living Licensure',
    statute: 'MN Statutes Chapter 144G',
    jurisdiction: 'state',
    authority: 'MN DHS',
    memberRulesCount: 24,
    affectedWorkflowsCount: 14,
    description: 'Primary licensing statute for assisted living facilities in Minnesota. Covers staffing, resident rights, medication management, and facility operations.',
  },
  'rs-4-1-005': {
    name: 'MN 626.557 — Vulnerable Adults Act',
    statute: 'MN Statutes Chapter 626.557',
    jurisdiction: 'state',
    authority: 'MN DHS',
    memberRulesCount: 6,
    affectedWorkflowsCount: 3,
    description: 'Mandatory reporting requirements for abuse, neglect, and financial exploitation of vulnerable adults.',
  },
  'rs-4-1-001': {
    name: 'MN 245D — HCBS Licensing',
    statute: 'MN Statutes Chapter 245D',
    jurisdiction: 'state',
    authority: 'MN DHS',
    memberRulesCount: 18,
    affectedWorkflowsCount: 10,
    description: 'Home and community-based services licensing. Overlay for Medicaid waiver-funded residents.',
  },
  'rs-4-1-002': {
    name: 'MN 256B.0659 — HCBS Waiver',
    statute: 'MN Statutes 256B.0659',
    jurisdiction: 'state',
    authority: 'MN DHS',
    memberRulesCount: 10,
    affectedWorkflowsCount: 5,
    description: 'Home and community-based services waiver requirements for PCA/CFSS programs.',
  },
  'rs-4-1-003': {
    name: 'MN 256B.85 — CFSS/PCA Program',
    statute: 'MN Statutes 256B.85',
    jurisdiction: 'state',
    authority: 'MN DHS',
    memberRulesCount: 8,
    affectedWorkflowsCount: 4,
    description: 'Community First Services and Supports (CFSS) and Personal Care Assistance (PCA) program requirements.',
  },
  'rs-5-0-001': {
    name: 'FLSA — Federal Labor Standards',
    statute: '29 USC Chapter 8',
    jurisdiction: 'federal',
    authority: 'DOL WHD',
    memberRulesCount: 6,
    affectedWorkflowsCount: 4,
    description: 'Federal overtime, minimum wage, and recordkeeping requirements for all employers.',
  },
  'rs-5-1-001': {
    name: 'MN Labor Standards',
    statute: 'MN Statutes Chapters 177, 181, 182',
    jurisdiction: 'state',
    authority: 'MN DOLI',
    memberRulesCount: 8,
    affectedWorkflowsCount: 3,
    description: 'Minnesota minimum wage, sick leave, and workplace safety standards.',
  },
};

function jurisdictionIcon(jurisdiction: 'federal' | 'state' | 'payer') {
  switch (jurisdiction) {
    case 'federal': return <IconBuildingBank size={14} />;
    case 'state': return <IconBuilding size={14} />;
    case 'payer': return <IconScale size={14} />;
  }
}

function jurisdictionColor(jurisdiction: 'federal' | 'state' | 'payer'): string {
  switch (jurisdiction) {
    case 'federal': return 'red';
    case 'state': return 'orange';
    case 'payer': return 'green';
  }
}

export function RegulatoryStackStep({
  product,
  acknowledged,
  onAcknowledge,
}: RegulatoryStackStepProps) {
  const rulesets = product.requiredRulesets;

  const { federalCount, stateCount, unknownCount, totalRules, totalAffectedWorkflows } = useMemo(() => {
    let federal = 0;
    let state = 0;
    let unknown = 0;
    let rules = 0;
    let workflows = 0;

    for (const rsId of rulesets) {
      const details = RULESET_DETAILS[rsId];
      if (details) {
        if (details.jurisdiction === 'federal') federal++;
        else if (details.jurisdiction === 'state') state++;
        rules += details.memberRulesCount;
        workflows += details.affectedWorkflowsCount;
      } else {
        unknown++;
      }
    }

    return { federalCount: federal, stateCount: state, unknownCount: unknown, totalRules: rules, totalAffectedWorkflows: workflows };
  }, [rulesets]);

  return (
    <Stack gap="md">
      <Group gap="xs">
        <ThemeIcon variant="light" size="lg" color="red">
          <IconShieldCheck size={20} />
        </ThemeIcon>
        <div>
          <Text fw={600} size="lg">Regulatory Stack</Text>
          <Text size="sm" c="dimmed">
            Required legislation for {product.name}. These are legal requirements — they cannot be removed.
          </Text>
        </div>
      </Group>

      {/* Summary Badges */}
      <Group gap="xs">
        <Badge size="md" color="red" variant="light" leftSection={<IconLock size={12} />}>
          {rulesets.length} required ruleset{rulesets.length !== 1 ? 's' : ''}
        </Badge>
        {federalCount > 0 && (
          <Badge size="sm" color="red" variant="outline">
            {federalCount} federal
          </Badge>
        )}
        {stateCount > 0 && (
          <Badge size="sm" color="orange" variant="outline">
            {stateCount} state
          </Badge>
        )}
        <Badge size="sm" color="gray" variant="outline">
          {totalRules} member rules
        </Badge>
        <Badge size="sm" color="blue" variant="outline">
          {totalAffectedWorkflows} affected workflows
        </Badge>
      </Group>

      {/* RuleSet Cards */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {rulesets.map((rsId) => {
          const details = RULESET_DETAILS[rsId];
          if (!details) {
            return (
              <Paper key={rsId} p="sm" withBorder>
                <Group gap="xs">
                  <IconLock size={14} color="var(--mantine-color-gray-5)" />
                  <Text size="sm" fw={500}>{rsId}</Text>
                  <Badge size="xs" color="gray">Unknown</Badge>
                </Group>
              </Paper>
            );
          }

          return (
            <Paper
              key={rsId}
              p="sm"
              withBorder
              style={{
                borderLeftWidth: 3,
                borderLeftColor: `var(--mantine-color-${jurisdictionColor(details.jurisdiction)}-5)`,
              }}
            >
              <Stack gap="xs">
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap">
                    <IconLock size={14} color="var(--mantine-color-gray-5)" />
                    <Text size="sm" fw={600} truncate>
                      {details.name}
                    </Text>
                  </Group>
                  <Tooltip label={`${details.jurisdiction} — ${details.authority}`}>
                    <Badge
                      size="xs"
                      color={jurisdictionColor(details.jurisdiction)}
                      variant="light"
                      leftSection={jurisdictionIcon(details.jurisdiction)}
                    >
                      {details.jurisdiction}
                    </Badge>
                  </Tooltip>
                </Group>

                <Text size="xs" c="dimmed" lineClamp={2}>
                  {details.description}
                </Text>

                {details.statute && (
                  <Group gap="xs">
                    <IconGavel size={12} color="var(--mantine-color-gray-5)" />
                    <Text size="xs" c="dimmed" fs="italic">
                      {details.statute}
                    </Text>
                  </Group>
                )}

                <Group gap="xs">
                  <Badge size="xs" variant="outline">
                    {details.memberRulesCount} rules
                  </Badge>
                  <Badge size="xs" variant="outline" color="blue">
                    {details.affectedWorkflowsCount} workflows
                  </Badge>
                  <Badge size="xs" variant="outline" color="gray">
                    {rsId}
                  </Badge>
                </Group>
              </Stack>
            </Paper>
          );
        })}
      </SimpleGrid>

      {/* Acknowledgment */}
      <Divider />

      <Alert
        icon={<IconInfoCircle size={16} />}
        color="blue"
        variant="light"
      >
        <Text size="sm" mb="xs">
          These RuleSets are <Text span fw={600}>legally required</Text> for operating a
          {' '}{product.industry.replace(/_/g, ' ')} business in {product.jurisdiction}.
          They define which rules, workflows, and form fields are mandatory.
        </Text>
        <Text size="xs" c="dimmed">
          Additional RuleSets may be activated when you select funding sources (e.g., Medicaid adds 245D overlay).
        </Text>
      </Alert>

      <Checkbox
        label="I acknowledge the regulatory requirements for this product"
        checked={acknowledged}
        onChange={(event) => onAcknowledge(event.currentTarget.checked)}
        size="md"
      />
    </Stack>
  );
}
